// export function getBrowserLocale(): string {
// return typeof window !== "undefined" && window.navigator.language ? window.navigator.language : "ko";
// }

import { format } from "date-fns/format";

/**
 * 브라우저의 언어 코드(로케일, 예: 'ko', 'en' 등)를 반환합니다.
 * window 객체가 없거나 값을 찾을 수 없을 땐 'ko'를 반환합니다.
 */
export function getBrowserLocaleCode(): string {
  return typeof window !== "undefined" && window.navigator.language
    ? window.navigator.language.split("-")[0]
    : "ko";
}

/**
 * 브라우저의 위치/설정에 따라 국가 코드를 반환합니다.
 * - window가 없을 땐 기본값 'kr'
 * - navigator.language 또는 navigator.languages 분석
 * - 국가 코드가 없는 경우 언어코드 맵핑 또는 'kr' 반환
 * @returns 국가 코드 (예: 'kr', 'us', 'jp' 등, 소문자)
 */
export function getBrowserCountryCode(): string {
  if (typeof window === "undefined") {
    return "kr"; // 서버 사이드 기본값
  }

  // 1. navigator.language에서 국가 코드 추출
  if (window.navigator.language) {
    const locale = window.navigator.language;
    const parts = locale.split("-");
    if (parts.length > 1) {
      return parts[1].toLowerCase();
    }
  }

  // 2. navigator.languages에서 첫 번째 언어에 국가 코드가 있으면 추출
  if (window.navigator.languages && window.navigator.languages.length > 0) {
    for (const lang of window.navigator.languages) {
      const parts = lang.split("-");
      if (parts.length > 1) {
        return parts[1].toLowerCase();
      }
    }
  }

  // 3. 언어코드를 기준으로 대표 국가코드 추정
  const language = getBrowserLocaleCode();
  const languageToCountryMap: { [key: string]: string } = {
    // 아시아
    ja: "jp", // 일본
    zh: "cn", // 중국
    zh_cn: "cn", // 중국 (간체)
    zh_tw: "tw", // 대만
    zh_hk: "hk", // 홍콩
    th: "th", // 태국
    vi: "vn", // 베트남
    id: "id", // 인도네시아
    ms: "my", // 말레이시아
    fil: "ph", // 필리핀
    hi: "in", // 인도
    bn: "bd", // 방글라데시
    ur: "pk", // 파키스탄
    ar: "sa", // 사우디아라비아
    he: "il", // 이스라엘
    tr: "tr", // 터키
    fa: "ir", // 이란

    // 유럽
    en: "us", // 영어 (미국)
    en_gb: "gb", // 영국
    en_ie: "ie", // 아일랜드
    en_au: "au", // 호주
    en_ca: "ca", // 캐나다
    en_nz: "nz", // 뉴질랜드
    en_za: "za", // 남아공
    fr: "fr", // 프랑스
    fr_ca: "ca", // 캐나다(프랑스)
    fr_be: "be", // 벨기에
    fr_ch: "ch", // 스위스
    de: "de", // 독일
    de_at: "at", // 오스트리아
    de_ch: "ch", // 스위스(독일)
    de_lu: "lu", // 룩셈부르크
    it: "it", // 이탈리아
    it_ch: "ch", // 스위스(이탈리아)
    es: "es", // 스페인
    es_mx: "mx", // 멕시코
    es_ar: "ar", // 아르헨티나
    es_cl: "cl", // 칠레
    es_co: "co", // 콜롬비아
    es_pe: "pe", // 페루
    es_ve: "ve", // 베네수엘라
    pt: "pt", // 포르투갈
    pt_br: "br", // 브라질
    pt_ao: "ao", // 앙골라
    pt_mz: "mz", // 모잠비크
    ru: "ru", // 러시아
    ru_by: "by", // 벨라루스
    ru_kz: "kz", // 카자흐스탄
    ru_ua: "ua", // 우크라이나
    nl: "nl", // 네덜란드
    nl_be: "be", // 벨기에(네덜란드어)
    sv: "se", // 스웨덴
    sv_fi: "fi", // 핀란드(스웨덴어)
    da: "dk", // 덴마크
    no: "no", // 노르웨이
    fi: "fi", // 핀란드
    pl: "pl", // 폴란드
    cs: "cz", // 체코
    sk: "sk", // 슬로바키아
    hu: "hu", // 헝가리
    ro: "ro", // 루마니아
    bg: "bg", // 불가리아
    hr: "hr", // 크로아티아
    sr: "rs", // 세르비아
    sl: "si", // 슬로베니아
    et: "ee", // 에스토니아
    lv: "lv", // 라트비아
    lt: "lt", // 리투아니아
    el: "gr", // 그리스
    mt: "mt", // 몰타
    cy: "cy", // 키프로스

    // 아메리카
    en_us: "us", // 미국
    es_us: "us", // 미국(스페인어)

    // 아프리카
    ar_eg: "eg", // 이집트
    ar_ma: "ma", // 모로코
    ar_dz: "dz", // 알제리
    ar_tn: "tn", // 튀니지
    ar_ly: "ly", // 리비아
    ar_sd: "sd", // 수단
    ar_jo: "jo", // 요르단
    ar_lb: "lb", // 레바논
    ar_sy: "sy", // 시리아
    ar_iq: "iq", // 이라크
    ar_kw: "kw", // 쿠웨이트
    ar_ae: "ae", // 아랍에미리트
    ar_qa: "qa", // 카타르
    ar_bh: "bh", // 바레인
    ar_om: "om", // 오만
    ar_ye: "ye", // 예멘
    ar_ps: "ps", // 팔레스타인
    sw: "tz", // 탄자니아
    sw_ke: "ke", // 케냐
    sw_ug: "ug", // 우간다
    ha: "ng", // 나이지리아
    ha_gh: "gh", // 가나
    ha_ne: "ne", // 니제르
    ha_cm: "cm", // 카메룬
    am: "et", // 에티오피아
    am_er: "er", // 에리트레아
    so: "so", // 소말리아
    so_et: "et", // 에티오피아(소말리어)
    so_ke: "ke", // 케냐(소말리어)
    zu: "za", // 남아공(줄루어)
    xh: "za", // 남아공(코사어)
    af: "za", // 남아공(아프리칸스어)
    af_na: "na", // 나미비아

    // 오세아니아
    en_fj: "fj", // 피지
    en_pg: "pg", // 파푸아뉴기니
    en_sb: "sb", // 솔로몬제도
    en_vu: "vu", // 바누아투
    en_nc: "nc", // 뉴칼레도니아
    en_pf: "pf", // 프랑스령폴리네시아
    en_ck: "ck", // 쿡제도
    en_ws: "ws", // 사모아
    en_to: "to", // 통가
    en_nu: "nu", // 니우에
    en_tk: "tk", // 토켈라우
    en_pw: "pw", // 팔라우
    en_mh: "mh", // 마셜제도
    en_fm: "fm", // 미크로네시아
    en_ki: "ki", // 키리바시
    en_tv: "tv", // 투발루
    en_nr: "nr", // 나우루
    en_pn: "pn", // 핏케언제도
  };

  return languageToCountryMap[language] || "kr";
}

/**
 * 브라우저의 현재 시간대 정보를 반환합니다.
 * - window가 존재하지 않으면 'Asia/Seoul' 반환
 * @returns IANA 시간대 이름 문자열 (예: 'Asia/Seoul', 'America/New_York')
 */
export function getBrowserTimeZone(): string {
  if (typeof window === "undefined") {
    return "Asia/Seoul"; // 서버 사이드 기본값
  }

  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    return "Asia/Seoul";
  }
}

/**
 * 서버에서 오는 시간은 해당 사용자의 로컬 시간의  utc 기준이므로 변환 필요
 *
 * @param fromServerDate 서버에서 오는 시간
 * @returns 로컬 시간
 */
export function serverDateToLocalDate(fromServerDate: string | undefined | null): Date | null {
  if (fromServerDate === undefined || fromServerDate === null) {
    return null;
  }

  const toLocalDate = new Date(
    new Date(fromServerDate).toLocaleString("en-US", { timeZone: "UTC" })
  );
  return toLocalDate;
}

/**
 * Date 객체를 yyyy-mm-dd 형식 문자열로 변환합니다.
 * @param date Date 객체
 * @returns yyyy-mm-dd 형식 문자열
 */
export function formatLocalToYyyyMmDd(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Date 객체를 HH:mm(24시간) 형식으로 반환합니다.
 * @param date Date 객체
 * @returns HH:mm 형식 문자열
 */
export function formatLocalToHHmm(date: Date | null | undefined): string {
  if (date === null || date === undefined) {
    return "NaN:NaN";
  }

  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

/**
 * Date 객체를 yyyy.mm.dd hh:mm:ss 형식 문자열로 변환합니다.
 * @param date Date 객체
 * @returns yyyy.mm.dd hh:mm:ss 형식 문자열
 */
export function formatLocalToYyyyMmDdHhMmSs(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}.${month}.${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * Date 객체를 yyyy.mm.dd hh:mm 형식 문자열로 변환합니다.
 * @param date Date 객체
 * @returns yyyy.mm.dd hh:mm 형식 문자열
 */
export function formatLocalToYyyyMmDdHhMm(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}.${month}.${day} ${hours}:${minutes}`;
}

export function formatLocalToHhMm(date: Date): string {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

/**
 * 'YYYY-MM-DD' 형식 문자열 날짜를 주어진 언어 코드에 맞게 포맷팅합니다.
 * @param dateString YYYY-MM-DD 형식의 날짜 문자열
 * @param locale 언어 코드 (예: 'ko', 'en', 'ja', 'zh' 등)
 * @returns 언어에 맞는 날짜 문자열
 */
export function formatDateToLocale(dateString: string, locale: string = "ko"): string {
  // 연/월/일 분리
  const [year, month, day] = dateString.split("-");
  const yearNum = parseInt(year, 10);
  const monthNum = parseInt(month, 10);
  const dayNum = parseInt(day, 10);

  // 언어별 사용자 친화 날짜 포맷
  switch (locale) {
    case "ko":
      return `${yearNum}년 ${monthNum}월 ${dayNum}일`;

    case "ja":
      return `${yearNum}年${monthNum}月${dayNum}日`;

    case "zh":
    case "zh-cn":
      return `${yearNum}年${monthNum}月${dayNum}日`;

    case "zh-tw":
      return `${yearNum}年${monthNum}月${dayNum}日`;

    case "en":
    case "en-us":
      return `${monthNum}/${dayNum}/${yearNum}`;

    case "en-gb":
    case "en-au":
      return `${dayNum}/${monthNum}/${yearNum}`;

    case "fr":
      return `${dayNum}/${monthNum}/${yearNum}`;

    case "de":
      return `${dayNum}.${monthNum}.${yearNum}`;

    case "es":
      return `${dayNum}/${monthNum}/${yearNum}`;

    case "it":
      return `${dayNum}/${monthNum}/${yearNum}`;

    case "pt":
    case "pt-br":
      return `${dayNum}/${monthNum}/${yearNum}`;

    case "ru":
      return `${dayNum}.${monthNum}.${yearNum}`;

    case "ar":
      return `${dayNum}/${monthNum}/${yearNum}`;

    case "hi":
      return `${dayNum}/${monthNum}/${yearNum}`;

    case "th":
      return `${dayNum}/${monthNum}/${yearNum + 543}`; // 태국은 불교력 사용

    case "vi":
      return `${dayNum}/${monthNum}/${yearNum}`;

    case "id":
    case "ms":
      return `${dayNum}/${monthNum}/${yearNum}`;

    case "tr":
      return `${dayNum}.${monthNum}.${yearNum}`;

    case "pl":
      return `${dayNum}.${monthNum}.${yearNum}`;

    case "nl":
      return `${dayNum}-${monthNum}-${yearNum}`;

    case "sv":
    case "da":
    case "no":
      return `${yearNum}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;

    default:
      // 기본값: 미국식
      return `${monthNum}/${dayNum}/${yearNum}`;
  }
}

/**
 * 브라우저 언어 설정에 맞춰 'YYYY-MM-DD'를 포맷팅합니다.
 * @param dateString YYYY-MM-DD 형식 날짜 문자열
 * @returns 브라우저 언어에 맞는 날짜 형식 문자열
 */
export function formatDateToLocaleBrowser(dateString: string): string {
  const locale = getBrowserLocaleCode();
  return formatDateToLocale(dateString, locale);
}

//MARK:- Date to Server
/**
 * 브라우저의 현재 시간을 서버 시간 형식으로 변환합니다.
 * yyyy-MM-dd'T'HH:mm:ss.SSSXXX
 */
export function dateToServerDate(): string {
  return format(new Date(), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`);
}
